import { formatCurrency } from '@shared/formatCurrency';

// Helper function to format date as "MMM d, yyyy" (e.g., "Jan 1, 2025")
function formatDate(date: Date | null | undefined): string {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Helper function to format date range (e.g., "Dec 16, 2025 - Jan 16, 2026")
function formatDateRange(start: Date | null | undefined, end: Date | null | undefined): string {
  if (!start || !end) return '';
  const startStr = new Date(start).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const endStr = new Date(end).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startStr} - ${endStr}`;
}

// Helper function to calculate days remaining in billing cycle
function calculateDaysRemaining(
  periodStart: Date | null | undefined,
  periodEnd: Date | null | undefined,
): number | null {
  if (!periodEnd) return null;
  const start = periodStart ? new Date(periodStart) : new Date();
  const end = new Date(periodEnd);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

interface BillingImpactCardProps {
  billingPreview: BillingImpact;
  className?: string;
}

/**
 * Reusable component to display billing impact information for subscription changes.
 * Shows current total, new total, and prorated charge upon acceptance.
 */
export function BillingImpactCard({ billingPreview, className = '' }: BillingImpactCardProps) {
  return (
    <div className={`rounded-md border bg-white ${className}`}>
      {/* Billing Summary Title */}
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-medium text-gray-900">Billing Summary</h3>
      </div>

      {/* Billing Details */}
      <div className="space-y-3 p-4">
        {/* New Monthly Subscription */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900">
              New Total
              {billingPreview.nextInvoiceDate && (
                <> (Starting {formatDate(billingPreview.nextInvoiceDate)})</>
              )}
            </h4>
            <p className="mt-1 text-xs text-gray-700">
              {billingPreview.newQuantity} advisor{' '}
              {billingPreview.newQuantity === 1 ? 'seat' : 'seats'} (
              {formatCurrency(billingPreview.unitPricePerSeat, 2)} × {billingPreview.newQuantity})
            </p>
          </div>
          <div className="text-right">
            <span className="text-base font-semibold text-gray-900">
              {billingPreview.nextInvoiceAmount
                ? formatCurrency(billingPreview.nextInvoiceAmount, 2)
                : formatCurrency(billingPreview.newMonthlyCharge, 2)}
            </span>
          </div>
        </div>

        {/* Charge Upon Acceptance */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900">Due Upon Acceptance</h4>
            {(() => {
              const daysRemaining = calculateDaysRemaining(
                billingPreview.prorationDate,
                billingPreview.billingPeriodEnd,
              );
              const dateRange = formatDateRange(
                billingPreview.prorationDate,
                billingPreview.billingPeriodEnd,
              );
              return (
                <p className="mt-1 text-xs italic text-gray-700">
                  {daysRemaining !== null
                    ? `Prorated for ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining in current billing cycle`
                    : 'Prorated for remaining days in current billing cycle'}
                  {dateRange && <> ({dateRange})</>}
                </p>
              );
            })()}
          </div>
          <span className="text-primary-dark text-base font-semibold">
            {formatCurrency(billingPreview.prorationAmount, 2)}
          </span>
        </div>
      </div>
    </div>
  );
}
